import { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const faqs = [
  {
    q: 'Meu produto é complexo demais. A IA vai conseguir responder certo?',
    a: 'A IA é treinada especificamente no seu catálogo, com suas especificações técnicas, tabela de preços e regras de negócio. Se seu vendedor consegue responder, a IA também consegue — só que instantaneamente, 24 horas por dia. E se o cliente fizer uma pergunta fora do escopo, o agente transfere automaticamente para um humano.',
  },
  {
    q: 'Já tentei chatbot antes e espantou meus clientes.',
    a: 'Chatbot de botão é completamente diferente de agente de IA. Chatbot antigo oferece opções rígidas e respostas genéricas. O agente Automat conversa naturalmente, entende contexto, áudio e até fotos. Seus clientes não vão perceber que é uma IA.',
  },
  {
    q: 'Quanto tempo demora pra configurar?',
    a: 'O onboarding completo leva de 5 a 10 dias úteis. Nosso time configura tudo com você: conecta o WhatsApp, alimenta o catálogo, treina a IA nas regras do seu negócio e faz testes antes de ativar.',
  },
  {
    q: 'E se o cliente quiser falar com um humano?',
    a: 'O agente detecta automaticamente quando o cliente precisa de atendimento humano — negociação complexa, reclamação, dúvida fora do escopo — e transfere a conversa para o atendente certo, com todo o contexto preservado.',
  },
  {
    q: 'Funciona com WhatsApp Business que já uso?',
    a: 'Sim. A Automat se conecta ao seu número existente. Você não precisa trocar de número nem avisar seus clientes.',
  },
  {
    q: 'Vou ficar preso em contrato longo?',
    a: 'Não. Os planos são mensais. Se em 30 dias você não vir valor, cancela sem burocracia.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const { ref, visible } = useScrollAnimation(0.08);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? -1 : i);

  return (
    <section id="faq" className="bg-b2bgray py-24 lg:py-32 px-6">
      <div className="max-w-3xl mx-auto" ref={ref}>
        {/* Badge */}
        <div
          className={`flex justify-center mb-5 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="font-b2bbody text-xs font-semibold tracking-widest uppercase border border-coral/30 text-coral rounded-full px-4 py-1.5">
            Dúvidas Frequentes
          </span>
        </div>

        {/* Title */}
        <div
          className={`text-center mb-12 transition-all duration-700 delay-75 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-b2b text-3xl sm:text-4xl font-bold text-navy leading-tight">
            Perguntas Frequentes
          </h2>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`bg-white rounded-2xl overflow-hidden border transition-all duration-500 cursor-pointer ${
                  isOpen ? 'border-coral/30' : 'border-gray-100 hover:border-gray-200'
                } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: visible ? `${i * 60 + 200}ms` : '0ms' }}
                onClick={() => toggle(i)}
              >
                {/* Question row */}
                <div className="flex items-start gap-4 px-6 py-5">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                      isOpen ? 'bg-coral' : 'bg-gray-100'
                    }`}
                  >
                    <i
                      className={`text-xs transition-transform duration-300 ${
                        isOpen ? 'ri-subtract-line text-white' : 'ri-add-line text-gray-500'
                      }`}
                    />
                  </div>
                  <p
                    className={`font-b2b text-base font-semibold leading-snug transition-colors duration-200 flex-1 ${
                      isOpen ? 'text-navy' : 'text-gray-700'
                    }`}
                  >
                    {faq.q}
                  </p>
                </div>

                {/* Answer */}
                <div
                  className="overflow-hidden transition-all duration-400"
                  style={{ maxHeight: isOpen ? '400px' : '0px' }}
                >
                  <div className="px-6 pb-5 pl-16">
                    <p className="font-b2bbody text-gray-500 text-sm leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}